"use client";

import React, {
  startTransition,
  useActionState,
  useEffect,
  useState,
} from "react";
import { submitWishForm } from "@/actions/wish-form";
import { WISH_INITIAL_STATE } from "@/services/form.service";
import ZipCodeInput from "../inputs/ZipCodeInput";
import ConfirmModal from "../ConfirmModal";
import { motion } from "motion/react";
import {
  NamespaceCoco,
  WishDefinitionFraction,
  IntervalFraction,
} from "@/utils/gql/graphql";
import RangeSlider from "../sliders/RangeSlider";
import TextAreaInput from "../inputs/TextAreaInput";
import TextInput from "../inputs/TextInput";
import CheckboxInput from "../inputs/CheckboxInput";
import FormLayout from "../inputs/FormLayout";
import { getPaddings } from "@/utils/theme-helper";
import { ZipCode, ZipCodeGroup } from "@/types/zipcode";
import ZodErrors from "../ZodErrors";
import StoreSelect from "../inputs/StoreSelect";
import { ApplicationDataType } from "@/services/queries/get-application-details";
import { formatPhoneNumberObject } from "@/utils/helper";

export interface OptionItemProps {
  option: {
    selected: boolean;
    id: string;
    text: string;
  };
  setEnabled: (checked: boolean) => void;
}

export interface WishTab {
  id: string;
  label: string;
  config: WishDefinitionFraction & { ranges: any[] };
}

interface IntegerRangeModel {
  lowerValue?: number;
  upperValue?: number;
}

const WishForm: React.FC<{
  config: {
    cmsData: any;
    app: ApplicationDataType;
    wishes: {
      wishNamespace: string;
      title: string;
      estateNamespaces: {
        displayName: string;
        namespace: string;
      }[];
      rangeCriterias: {
        displayName: string;
        namespace: string;
      }[];
    }[];
    overskrift: string;
    formPadding: string;
    tekst: string;
    submitLabel: string;
    resourceGroupId?: string;
  };
  recaptchaSiteKey: string;
}> = ({ config, recaptchaSiteKey }) => {
  const { cmsData, app } = config;
  const zipCodeGroups = app.intervals?.[0].items.map(
    (item: IntervalFraction) => ({
      ...item,
      isGroup: true as const,
    })
  );

  const wishTabs = config.wishes.map((item) => {
    const wishDefinition = app.wishDefinitions?.find(
      (x: WishDefinitionFraction) => x.namespace === item.wishNamespace
    );

    const ranges = wishDefinition?.rangeNamespaces
      ?.map((criteria: NamespaceCoco) =>
        app.ranges?.find((y: any) => y.instanceNamespace === criteria.namespace)
      )
      .filter((x: any) => !!x);

    return {
      id: item.wishNamespace,
      label: item.title,
      config: {
        ...wishDefinition,
        ranges,
      },
    };
  });

  const [formHash, setFormHash] = useState<string>("");
  const [selectedWishTab, setSelectedWishTab] = useState<WishTab>(
    wishTabs[0] as any
  );
  const [selectedEstateNamespaces, setSelectedEstateNamespaces] = useState<
    string[]
  >([]);
  const [selectedZipCodes, setSelectedZipCodes] = useState<
    (ZipCode | ZipCodeGroup)[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);

  const [formState, formAction, isPending] = useActionState(
    async (prevState: any, formData: FormData) => {
      if (selectedEstateNamespaces.length === 0) {
        return {
          ...prevState,
          zodErrors: {
            estateNamespaces: ["Der skal foretages minimum et valg"],
          },
          isSuccess: false,
        };
      }

      // Check for zipcode selection
      if (selectedZipCodes.length === 0) {
        return {
          ...prevState,
          zodErrors: {
            zipcodes: [
              "Vælg venligst mindst ét postnummer eller postnummerinterval",
            ],
          },
          isSuccess: false,
        };
      }

      try {
        const zipCodeRanges: IntegerRangeModel[] = selectedZipCodes
          .filter((item): item is ZipCodeGroup => item.isGroup)
          .map((group) => ({
            lowerValue: group.from,
            upperValue: group.to,
          }));
        const individualZipCodes = selectedZipCodes
          .filter((item): item is ZipCode => !item.isGroup)
          .map((zip) => zip.nr);

        Object.entries(formData).forEach(([key, value]) => {
          if (key.includes("Range")) {
            formData.set(key, value.toString());
          }
        });

        formData.append("zipCodeRanges", JSON.stringify(zipCodeRanges));
        formData.append("zipcodes", individualZipCodes.join(","));
        formData.append("estateNamespaces", selectedEstateNamespaces.join(","));
        formData.append("wishNamespace", selectedWishTab.config!.namespace!);

        return submitWishForm(prevState, formData).then((response) => {
          if (response.isSuccess) {
            setSelectedEstateNamespaces([]);
            setSelectedZipCodes([]);
            setSelectedWishTab({ ...selectedWishTab });
            setFormHash(Math.random().toString(36).substring(2, 15));
          }
          return response;
        });
      } catch (error) {
        console.error("Form submission error:", error);
        return {
          ...prevState,
          apiErrors: "Der opstod en fejl ved indsendelse af formularen",
          isSuccess: false,
        };
      }
    },
    WISH_INITIAL_STATE
  );

  const submit = (payload: FormData) => {
    (async () => {
      try {
        const token = await grecaptcha.enterprise.execute(recaptchaSiteKey, {
          action: "WishForm",
        });
        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);

  useEffect(() => {
    if (formState?.zodErrors && Object.keys(formState.zodErrors).length > 0) {
      return;
    }

    setSelectedEstateNamespaces([]);
    setFormHash(Math.random().toString(36).substring(2, 15));
  }, [selectedWishTab, formState?.zodErrors]);

  const {
    firstName,
    lastName,
    address,
    zip,
    city,
    phone,
    email,
    internalNote,
  } = formState?.data || {};
  return (
    <div>
      <ConfirmModal
        description="Du kan altid kontakte os på:"
        title="Du er nu tilmeldt køberkartoteket."
        setOpen={(isOpen) => {
          setIsOpen(isOpen);
        }}
        isOpen={isOpen}
        contactInfo={{
          phone: formatPhoneNumberObject(formState.contactInfo?.phone ?? ""),
          email: formState.contactInfo?.email?.address ?? "",
        }}
      />
      <ConfirmModal
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />

      <div className="flex flex-col flex-1 lg-w-2/3">
        {wishTabs.length > 1 && (
          <nav className="bg-[var(--tabs-bg-color)]">
            <ul className="flex gap-4 overflow-auto">
              {wishTabs.map((item, index) => {
                const isSelected = selectedWishTab.id === item.id;
                return (
                  <li
                    key={index}
                    role="tab"
                    className="relative"
                    aria-selected={isSelected}
                  >
                    {isSelected ? (
                      <motion.div
                        layoutId="selected"
                        className="absolute bg-[var(--tabs-active-bg-color)] inset-0 z-1"
                      />
                    ) : null}
                    <motion.button
                      data-active={isSelected}
                      className="z-2 homepage-text-sm relative cursor-pointer p-4 data-[active=false]:text-[var(--tabs-text-color)] data-[active=true]:text-[var(--tabs-active-text-color)]"
                      onTapStart={() =>
                        setSelectedWishTab(
                          wishTabs.find((x) => x.id === item.id) as any
                        )
                      }
                      whileTap={{ scale: 0.9 }}
                      whileFocus={{
                        backgroundColor: "var(--accent-transparent)",
                      }}
                    >
                      {item?.label}
                    </motion.button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
        <div
          className={`bg-[var(--tabs-active-bg-color)] ${getPaddings(config.formPadding)}`}
        >
          <FormLayout
            submitLabel={config.submitLabel ?? "Send"}
            featureFlags={cmsData?.featureFlags}
            isPending={isPending}
            formAction={submit}
          >
            {config.resourceGroupId ? (
              <input
                type="hidden"
                name="resourceGroupId"
                value={config.resourceGroupId}
              />
            ) : (
              <StoreSelect
                cmsData={cmsData}
                label="Vælg butik"
                name="resourceGroupId"
                stores={app.stores}
              />
            )}
            <ZipCodeInput
              cmsData={cmsData}
              errors={formState?.zodErrors?.zipcodes}
              onChange={(item) => {
                setSelectedZipCodes(item);
              }}
              selectedZipCodes={selectedZipCodes}
              zipCodeGroups={zipCodeGroups!}
            >
              <ZodErrors errors={formState?.zodErrors?.zipcodes} />
            </ZipCodeInput>
            <div
              className={`grid grid-cols-2 col-span-6 md:grid-cols-3 group ${cmsData.forms.style?.type?.startsWith("material") ? "" : "gap-4"}`}
            >
              {selectedWishTab.config?.estatedNamespaces?.map((x: any) => (
                <CheckboxInput
                  cmsData={cmsData}
                  key={x.namespace}
                  label={x.displayName}
                  isChecked={selectedEstateNamespaces.includes(x.namespace)}
                  onChanged={(checked) => {
                    if (checked) {
                      setSelectedEstateNamespaces([
                        ...selectedEstateNamespaces,
                        x.namespace,
                      ]);
                    } else {
                      setSelectedEstateNamespaces(
                        selectedEstateNamespaces.filter(
                          (item) => item !== x.namespace
                        )
                      );
                    }
                  }}
                />
              ))}
              <ZodErrors errors={formState?.zodErrors?.estateNamespaces} />
            </div>
            {selectedWishTab.config?.ranges?.map((x) => {
              if (
                selectedWishTab.config?.namespace ===
                  "dk.realequity.wish.erhverv" &&
                x.instanceNamespace === "dk.realequity.ie.range.boligstoerrelse"
              ) {
                return null;
              }

              return (
                <RangeSlider
                  key={`${x.instanceNamespace}-${formHash}`}
                  range={x}
                />
              );
            })}
            <TextAreaInput
              cmsData={cmsData}
              name="internalNote"
              label="Øvrige ønsker til ejendommen"
              defaultValue={internalNote}
              errors={formState?.zodErrors?.internalNote}
              required={false}
            />
            <TextInput
              cmsData={cmsData}
              name="firstName"
              label="Fornavn"
              defaultValue={firstName}
              errors={formState?.zodErrors?.firstName}
              required={cmsData?.forms.nameRequired}
            />
            <TextInput
              cmsData={cmsData}
              name="lastName"
              label="Efternavn"
              defaultValue={lastName}
              errors={formState?.zodErrors?.lastName}
              required={cmsData?.forms.nameRequired}
            />
            <TextInput
              cmsData={cmsData}
              className="col-span-6"
              name="address"
              label="Adresse"
              defaultValue={address}
              errors={formState?.zodErrors?.address}
              required={cmsData?.forms.addressRequired}
            />
            <TextInput
              cmsData={cmsData}
              className="col-span-2"
              name="zip"
              label="Postnr"
              defaultValue={zip}
              errors={formState?.zodErrors?.zip}
              required={cmsData?.forms.zipRequired}
            />
            <TextInput
              cmsData={cmsData}
              className="col-span-4"
              name="city"
              label="By"
              defaultValue={city}
              errors={formState?.zodErrors?.city}
              required={cmsData?.forms.cityRequired}
            />
            <TextInput
              cmsData={cmsData}
              className="col-span-3"
              name="phone"
              label="Telefonnummer"
              defaultValue={phone}
              errors={formState?.zodErrors?.phone}
              required={cmsData?.forms.phoneRequired}
            />
            <TextInput
              cmsData={cmsData}
              className="col-span-3"
              name="email"
              label="E-mail"
              defaultValue={email}
              errors={formState?.zodErrors?.email}
              required={cmsData?.forms.emailRequired}
            />
          </FormLayout>
        </div>
      </div>
    </div>
  );
};

export default WishForm;
